import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '@/shared/utils/cn';
import { Button, type ButtonProps } from './Button';
import { Tooltip } from './Tooltip';

const iconButtonSize = cva('shrink-0 px-0', {
  variants: {
    iconSize: {
      sm: 'size-9 [@media(pointer:coarse)]:size-11',
      md: 'size-11',
      lg: 'size-12',
    },
    round: { true: 'rounded-full', false: '' },
  },
  defaultVariants: { iconSize: 'md', round: false },
});

export interface IconButtonProps
  extends
    Omit<ButtonProps, 'size' | 'children' | 'aria-label'>,
    VariantProps<typeof iconButtonSize> {
  /** Nombre accesible (obligatorio: el botón no tiene texto visible). */
  label: string;
  icon: ReactNode;
  /** Muestra el nombre como tooltip (escritorio y teclado). */
  tooltip?: boolean;
  /** Estado de un botón de alternancia (aria-pressed). */
  pressed?: boolean;
}

/** Botón solo-icono de 44 px (36 px en punteros finos si es `sm`). */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, tooltip = false, pressed, iconSize, round, variant = 'ghost', className, ...rest },
  ref,
) {
  const button = (
    <Button
      ref={ref}
      variant={variant}
      aria-label={label}
      aria-pressed={pressed}
      className={cn(iconButtonSize({ iconSize, round }), className)}
      {...rest}
    >
      {icon}
    </Button>
  );
  return tooltip ? <Tooltip content={label}>{button}</Tooltip> : button;
});
